/**
 * Shadow roles (P1-T10, SPEC-01 §6).
 *
 * LOD0 cell meshes cast directly (cells are already 128 m). Ground quads
 * (`grd_*` meshes from the exporter, road/walk/deck/plaza materials) and
 * water never cast; LOD1 proxies and the skyline ring never cast. Everything
 * solid receives. On Low there is no shadow map; people get blob shadows
 * instead (a radial dark decal that tracks the entity).
 */
import type { AbstractMesh, Mesh, Scene } from 'babylonjs';
import { addCaster, type ShadowHandle } from '../engine/shadows';

/** true when the mesh must never cast (ground, water, glass glow, proxies). */
export function isNonCaster(mesh: AbstractMesh): boolean {
  const n = mesh.name.toLowerCase();
  if (n.startsWith('grd_') || n.includes('water') || n.includes('lod1') || n.includes('skyline')) return true;
  const m = mesh.material;
  if (!m) return false;
  const mn = (m.name ?? '').toLowerCase();
  if (mn.includes('water') || mn.includes('proxy') || mn.includes('sign') || mn.includes('glass'))
    return true;
  if ((m as unknown as { disableLighting?: boolean }).disableLighting === true) return true; // glow stuff
  return false;
}

/** Assign cast/receive roles to a freshly loaded cell container. */
export function applyShadowRoles(
  shadow: ShadowHandle | null,
  meshes: AbstractMesh[],
  receive: boolean,
): void {
  for (const mesh of meshes) {
    if (mesh.getTotalIndices() === 0) continue;
    mesh.receiveShadows = receive;
    if (shadow && !isNonCaster(mesh)) addCaster(shadow, mesh);
  }
}

/** Radial blob shadow field for the Low tier (people only in P1). */
export class BlobShadows {
  private readonly root: Mesh;
  private readonly tracked = new Map<string, Mesh>();

  constructor(
    private readonly babylon: typeof import('babylonjs'),
    private readonly scene: Scene,
  ) {
    const B = this.babylon;
    // radial falloff in RGB on white — ALPHA_MUL keeps it dark over any ground
    const tex = new B.DynamicTexture('kn-blob', { width: 64, height: 64 }, this.scene, false);
    const ctx = tex.getContext();
    const g = ctx.createRadialGradient(32, 32, 2, 32, 32, 30);
    g.addColorStop(0, 'rgba(255,255,255,1)');
    g.addColorStop(0.65, 'rgba(255,255,255,0.55)');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, 64, 64);
    tex.update();
    const mat = new B.StandardMaterial('kn-blob-mat', this.scene);
    mat.diffuseColor = new B.Color3(0, 0, 0);
    mat.specularColor = new B.Color3(0, 0, 0);
    mat.emissiveColor = new B.Color3(0, 0, 0);
    mat.opacityTexture = tex;
    mat.backFaceCulling = false;
    mat.disableLighting = true;

    this.root = this.babylon.MeshBuilder.CreatePlane('kn-blob-root', { width: 1.5, height: 1.5 }, this.scene);
    this.root.material = mat;
    this.root.rotation.x = Math.PI / 2; // flat, normal up
    this.root.isPickable = false;
    this.root.applyFog = false;
    this.root.setEnabled(false);
  }

  /** Track an entity (id → capsule mesh). Each gets one blob quad (clone). */
  set(id: string, x: number, z: number, on: boolean): void {
    let blob = this.tracked.get(id);
    if (!on) {
      if (blob) blob.setEnabled(false);
      return;
    }
    if (!blob) {
      blob = this.root.clone(`kn-blob-${id}`);
      blob.parent = null;
      this.tracked.set(id, blob);
    }
    blob.setEnabled(true);
    // parent plane is rotated; clones share the transform → position in world
    blob.position.set(x, 0.03, z);
    blob.rotation.x = Math.PI / 2;
  }

  remove(id: string): void {
    const blob = this.tracked.get(id);
    if (blob) {
      blob.dispose();
      this.tracked.delete(id);
    }
  }

  dispose(): void {
    for (const b of this.tracked.values()) b.dispose();
    this.tracked.clear();
    this.root.dispose();
  }
}
